/**
 * Jwear header search enhancements:
 * - idle popular searches, clear button, status region
 * - mobile focus on open, reset on close
 * - predictive fetch reliability (debounce, stale guard, errors)
 */
(() => {
  'use strict';

  const MIN_QUERY = 2;
  const DEBOUNCE_MS = 200;
  const POPULAR = [
    { label: 'Shapewear', q: 'shapewear' },
    { label: 'Bodysuits', q: 'bodysuit' },
    { label: 'Bras', q: 'bra' },
    { label: 'Thigh slimmers', q: 'thigh slimmer' }
  ];

  const idleMarkup = () => {
    const links = POPULAR.map(
      (item) =>
        `<a href="${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(item.q)}&type=product" data-instant>${item.label}</a>`
    ).join('');
    return `<div class="jwear-search-idle" data-jw-search-idle>
      <p class="jwear-search-idle__eyebrow">Popular searches</p>
      <div class="jwear-search-idle__links" aria-label="Popular searches">${links}</div>
    </div>`;
  };

  const errorMarkup = () =>
    `<div class="jwear-search-error" role="alert">
      <p class="h6">Search unavailable</p>
      <p class="text-subdued">Check your connection and try again.</p>
      <button type="button" data-jw-search-retry>Retry</button>
    </div>`;

  function setStatus(headerSearch, state, message) {
    const status = headerSearch.querySelector('[data-jw-search-status]');
    const predictive = headerSearch.querySelector('predictive-search');
    if (predictive) predictive.dataset.jwStatus = state;

    if (!status) return;

    if (state === 'loading') {
      status.hidden = false;
      status.innerHTML = '<span class="header-search__spinner" aria-hidden="true"></span><span>Searching…</span>';
      status.setAttribute('aria-live', 'polite');
      return;
    }

    if (state === 'typing') {
      status.hidden = false;
      status.textContent = `Type ${MIN_QUERY} or more characters`;
      return;
    }

    if (state === 'error') {
      status.hidden = false;
      status.textContent = message || 'Something went wrong';
      return;
    }

    status.hidden = true;
    status.textContent = '';
  }

  function showIdle(resultsSlot) {
    if (!resultsSlot) return;
    resultsSlot.innerHTML = idleMarkup();
  }

  function syncClearButton(headerSearch, value) {
    const clearBtn = headerSearch.querySelector('[data-jw-search-clear]');
    if (!clearBtn) return;
    const hasValue = Boolean(value && value.length);
    clearBtn.setAttribute('aria-hidden', hasValue ? 'false' : 'true');
    clearBtn.tabIndex = hasValue ? 0 : -1;
  }

  function supportsPredictiveApi() {
    try {
      return JSON.parse(document.getElementById('shopify-features').innerHTML).predictiveSearch;
    } catch (e) {
      return false;
    }
  }

  function enhancePredictiveSearch(headerSearch, predictive, form, input, resultsSlot) {
    let abortController = null;
    let requestId = 0;
    let debounceTimer = null;

    const fetchResults = async () => {
      const query = input.value.trim();
      if (!query) {
        abortController?.abort();
        showIdle(resultsSlot);
        setStatus(headerSearch, 'idle');
        return;
      }

      if (query.length < MIN_QUERY) {
        abortController?.abort();
        resultsSlot.innerHTML = '';
        setStatus(headerSearch, 'typing');
        return;
      }

      abortController?.abort();
      abortController = new AbortController();
      const currentRequest = ++requestId;

      setStatus(headerSearch, 'loading');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const root = window.Shopify?.routes?.root || '/';
        const suggest = supportsPredictiveApi() ? '/suggest' : '';
        const params = new URLSearchParams({
          q: query,
          section_id: 'predictive-search',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each'
        });
        const response = await fetch(`${root}search${suggest}?${params}`, {
          signal: abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`Search failed (${response.status})`);
        if (currentRequest !== requestId) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');

        if (!section) throw new Error('Missing predictive section');

        resultsSlot.replaceChildren(...document.importNode(section, true).children);
        setStatus(headerSearch, 'idle');
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (currentRequest !== requestId) return;
        resultsSlot.innerHTML = errorMarkup();
        setStatus(headerSearch, 'error');
      } finally {
        if (currentRequest === requestId) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    };

    const scheduleFetch = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchResults, DEBOUNCE_MS);
    };

    input.addEventListener(
      'input',
      () => {
        syncClearButton(headerSearch, input.value);
        scheduleFetch();
      },
      { signal: headerSearch.abortController?.signal }
    );

    form.addEventListener(
      'submit',
      (event) => {
        if (!input.value.trim()) event.preventDefault();
      },
      { signal: headerSearch.abortController?.signal }
    );

    resultsSlot.addEventListener('click', (event) => {
      const retry = event.target.closest('[data-jw-search-retry]');
      if (retry) {
        event.preventDefault();
        scheduleFetch();
      }
    });

    showIdle(resultsSlot);
  }

  function initHeaderSearch(headerSearch) {
    if (!(headerSearch instanceof HTMLElement) || headerSearch.dataset.jwSearchInit === 'true') return;
    headerSearch.dataset.jwSearchInit = 'true';

    const form = headerSearch.querySelector('form');
    const input = headerSearch.querySelector('.header-search__input');
    const predictive = headerSearch.querySelector('[data-jw-predictive-search]');
    const resultsSlot = predictive?.querySelector('[data-jw-search-results]');
    const clearBtn = headerSearch.querySelector('[data-jw-search-clear]');

    if (!form || !input || !predictive || !resultsSlot) return;

    headerSearch.setAttribute('aria-label', headerSearch.getAttribute('aria-label') || 'Search');

    headerSearch.addEventListener('dialog:after-show', () => {
      input.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
      });
    });

    headerSearch.addEventListener('dialog:after-hide', () => {
      input.setAttribute('aria-expanded', 'false');
      form.reset();
      syncClearButton(headerSearch, '');
      showIdle(resultsSlot);
      setStatus(headerSearch, 'idle');
    });

    clearBtn?.addEventListener('click', () => {
      input.value = '';
      syncClearButton(headerSearch, '');
      input.focus({ preventScroll: true });
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    enhancePredictiveSearch(headerSearch, predictive, form, input, resultsSlot);
  }

  function initAll(root = document) {
    root.querySelectorAll('header-search').forEach(initHeaderSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
